import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSubscriptionHistory } from 'data/reducers/subscriptionReducer';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import './SubscriptionHistory.css';
import moment from 'moment/moment';
import { SubscriptionHelper } from 'view/helpers';
import { getSmsAddonPeriodCost } from './smsAddon';

const SubscriptionHistory = () => {
	const dispatch = useDispatch();
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(15);

	const {
		subscriptionHistory,
		isLoadingSubscriptionHistory,
		subscriptionHistoryError,
	} = useSelector((state) => state.subscription);

	// Only show if user has more than 1 subscription (including current and historical)
	const totalSubscriptions = subscriptionHistory?.total || 0;

	useEffect(() => {
		// Only fetch if we haven't loaded the data yet

		dispatch(fetchSubscriptionHistory(currentPage, perPage));
	}, [dispatch, currentPage, perPage]);

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	const handlePerPageChange = (newPerPage) => {
		setPerPage(newPerPage);
		setCurrentPage(1); // Reset to first page when changing per page
	};

	const getStatusBadgeClass = (status) => {
		return SubscriptionHelper.getSubscriptionStatusBadgeClass(status);
	};

	const getStatusDisplay = (status) => {
		return SubscriptionHelper.getSubscriptionStatusDisplay(status);
	};

	const formatPlanName = (plan) => {
		if (!plan || !plan.name) return 'Unknown Plan';

		// Use the plan name directly from the resource
		return plan.name;
	};

	const getFormattedAmount = (subscription) => {
		const plan = subscription?.plan;
		const interval = subscription?.interval || plan?.interval || 'monthly';
		const isYearly = interval === 'yearly';
		const basePrice = Number(plan?.price_dollars) || 0;
		if (subscription?.has_sms_addon && basePrice > 0) {
			const smsAddonCost = getSmsAddonPeriodCost(isYearly);
			const total = (basePrice + smsAddonCost).toFixed(2);
			return `$${total}`;
		}
		return plan?.formatted_price || (basePrice > 0 ? `$${basePrice.toFixed(2)}` : '—');
	};

	const getBillingCycle = (subscription) => {
		const interval =
			subscription?.plan?.interval || subscription?.interval || 'monthly';
		return ucfirst(interval);
	};

	const getSubscriptionDuration = (subscription) => {
		if (!subscription?.created_at) return '—';

		const interval =
			subscription?.plan?.interval || subscription?.interval || 'monthly';

		if (subscription.ends_at) {
			const months = moment(subscription.ends_at).diff(
				moment(subscription.created_at),
				'months',
			);
			if (months >= 12 && months % 12 === 0) {
				const years = months / 12;
				return years === 1 ? '1 year' : `${years} years`;
			}
			return months <= 1 ? '1 month' : `${months} months`;
		}

		if (subscription.stripe_status === 'active') {
			const elapsedMonths = moment().diff(
				moment(subscription.created_at),
				'months',
			);
			const elapsedText = elapsedMonths <= 1 ? '1 month' : `${elapsedMonths} months`;
			return `${elapsedText} (${interval === 'yearly' ? '1 yr plan' : 'Ongoing'})`;
		}

		return '—';
	};

	const ucfirst = (str) => {
		return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
	};

	// Don't render if user has only 1 or no subscriptions
	if (totalSubscriptions <= 1) {
		return null;
	}

	if (isLoadingSubscriptionHistory) {
		return (
			<div className="subscription-history-container">
				<div className="subscription-history-header">
					<h3>Subscription History</h3>
				</div>
				<div className="text-center py-5">
					<div className="spinner-border" role="status">
						<span className="sr-only">Loading subscription history...</span>
					</div>
				</div>
			</div>
		);
	}

	if (subscriptionHistoryError) {
		return (
			<div className="subscription-history-container">
				<div className="subscription-history-header">
					<h3>Subscription History</h3>
				</div>
				<div className="alert alert-danger">
					Error loading subscription history: {subscriptionHistoryError}
				</div>
			</div>
		);
	}

	return (
		<div className="subscription-history-container">
			<div className="subscription-history-header">
				<h3>Subscription History</h3>
				<div className="subscription-history-controls">
					<label className="per-page-label">
						Show:
						<select
							value={perPage}
							onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
							className="form-control form-control-sm ml-2"
						>
							<option value={10}>10</option>
							<option value={15}>15</option>
							<option value={25}>25</option>
							<option value={50}>50</option>
						</select>
						entries
					</label>
				</div>
			</div>

			{subscriptionHistory &&
			subscriptionHistory.data &&
			subscriptionHistory.data.length > 0 ? (
				<>
					<div className="table-responsive">
						<table className="table table-striped subscription-history-table">
							<thead>
								<tr>
									<th scope="col">Plan</th>
									<th scope="col">Status</th>
									<th scope="col">Start Date</th>
									<th scope="col">End Date</th>
									<th scope="col">Duration</th>
									<th scope="col">Amount</th>
									<th scope="col">Billing Cycle</th>
								</tr>
							</thead>
							<tbody>
								{subscriptionHistory.data.map((subscription) => (
									<tr key={subscription.id}>
										<td>
											<div className="subscription-plan">
												<div className="plan-name d-flex align-items-center flex-wrap gap-1">
													<span>{formatPlanName(subscription.plan)}</span>
													{subscription.has_sms_addon && (
														<span className="badge badge-success ml-1" style={{ fontSize: '0.7rem' }}>
															+ SMS Add-on ($19.99/mo)
														</span>
													)}
												</div>
											</div>
										</td>
										<td>
											<span
												className={`badge ${getStatusBadgeClass(
													subscription.stripe_status
												)}`}
											>
												{getStatusDisplay(subscription.stripe_status)}
											</span>
										</td>
										<td>
											<div className="subscription-date">
												{moment(subscription.created_at).format('MM-DD-YYYY')}
											</div>
										</td>
										<td>
											<div className="subscription-date">
												{subscription.ends_at
													? moment(subscription.ends_at).format('MM-DD-YYYY')
													: subscription.next_billing_date
													? moment(subscription.next_billing_date).format('MM-DD-YYYY')
													: subscription.stripe_status === 'active'
													? 'Renews'
													: '—'}
											</div>
										</td>
										<td>
											<div className="subscription-duration">
												{getSubscriptionDuration(subscription)}
											</div>
										</td>
										<td>
											<div className="subscription-amount">
												{getFormattedAmount(subscription)}
											</div>
										</td>
										<td>
											<div className="subscription-billing">
												{getBillingCycle(subscription)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					<div className="subscription-history-footer">
						<div className="subscription-history-info">
							Showing {subscriptionHistory.from} to {subscriptionHistory.to} of{' '}
							{subscriptionHistory.total} entries
						</div>

						{/* Same pagination as the rest of the v3 dashboard */}
						{subscriptionHistory.last_page > 1 && (
							<PaginatorV3
								currentPage={subscriptionHistory.current_page}
								totalPages={subscriptionHistory.last_page}
								onPageChange={handlePageChange}
							/>
						)}
					</div>
				</>
			) : (
				<div className="no-subscription-history">
					<div className="text-center py-5">
						<i className="fa fa-history fa-3x text-muted mb-3"></i>
						<h5>No Subscription History</h5>
						<p className="text-muted">
							You don't have any previous subscription history.
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default SubscriptionHistory;
